import { Spacing, Text } from '@vkontakte/vkui';
import image from 'img/subscriptions/cancel-autorenew-autoupload.png';
import imageMobile from 'img/subscriptions/cancel-autorenew-autoupload-mobile.png';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import styles from './CancelAutoRenewAutouploadModal.css';
import type { CancelAutoRenewAutouploadModalProps } from './CancelAutoRenewAutouploadModal.types';

export const CancelAutoRenewAutouploadModal = memo<CancelAutoRenewAutouploadModalProps>(
    ({ onClose, subscription, onCancel, onSuccess }): ReactElement => {
        const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);
        const scrollRef = useRef<HTMLDivElement | null>(null);

        const isMobile = useSelector(EnvironmentSelectors.isPhone);

        useEffect(() => {
            sendAnalytics(ESubscriptionModalAction.autoloadShow);
            emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_CANCEL_SHOW);
        }, []);

        const content = useMemo(
            (): ReactElement => (
                <>
                    <Spacing size={16} />
                    <Text className={styles.text}>
                        У вас есть файлы с опцией безлимитная автозагрузка. Без подписки она отключится и им может не хватить бесплатной
                        памяти
                    </Text>
                    <Spacing size={36} />
                </>
            ),
            []
        );

        const handleKeepAutoRenew = useCallback(() => {
            sendAnalytics(ESubscriptionModalAction.autoloadSave);
            emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_CANCEL_SAVE);
            onSuccess();
        }, [onSuccess]);

        const handleCancelAutoRenew = useCallback(() => {
            sendAnalytics(ESubscriptionModalAction.autoloadCancel);
            emitAnalyticEvent(AnalyticEventNames.AUTOUPLOAD_CANCEL);
            onCancel();
        }, [onCancel]);

        const handleClose = useCallback(() => {
            sendAnalytics(ESubscriptionModalAction.autoloadClose);
            onClose?.();
        }, [onClose]);

        if (isMobile) {
            return (
                <MobileDialog
                    id="cancel-autorenew-autoupload-mobile"
                    onClose={handleClose}
                    topmost
                    closeOnDimmerClick
                    scrollRef={scrollRef}
                    mod="base"
                    contextPadding="zero"
                    isRebrandingQuotaLanding
                >
                    <div ref={scrollRef}>
                        <img src={imageMobile} alt="Без подписки бесконечной памяти больше не будет" className={styles.mobileImg} />
                        <Spacing size={10} />
                        <Text className={styles.mobileTitle}>Без подписки бесконечной памяти больше не будет</Text>
                        <Spacing size={8} />
                        <Text className={styles.mobileText}>
                            У вас есть файлы с опцией безлимитная автозагрузка. Без подписки она отключится и&nbsp;им&nbsp;может не хватить
                            бесплатной памяти
                        </Text>
                        <Spacing size={24} />
                        <div className={styles.buttonWrapper}>
                            <Button
                                primary
                                theme="vk"
                                sizeMode={ButtonSizeMode.big}
                                onClick={handleKeepAutoRenew}
                                className={styles.button}
                            >
                                Сохранить подписку
                            </Button>
                            <Spacing size={12} />
                            <Button theme="vk" sizeMode={ButtonSizeMode.big} onClick={handleCancelAutoRenew} className={styles.button}>
                                Отключить всё равно
                            </Button>
                        </div>
                        <Spacing size={32} />
                    </div>
                </MobileDialog>
            );
        }

        return (
            <WhatsNewDialog
                onClose={handleClose}
                onClick={handleKeepAutoRenew}
                onSecondary={handleCancelAutoRenew}
                primaryButtonText="Сохранить подписку"
                secondaryButtonText="Отключить всё равно"
                title="Без подписки бесконечной памяти больше не будет"
                content={content}
                imageUrl={image}
                imageBgColor="var(--vkui--color_background)"
                buttonTheme="vk"
                dialogSize="tiny"
                qaId="cancel-autorenew-autoupload"
                isRebranding
            />
        );
    }
);

CancelAutoRenewAutouploadModal.displayName = 'CancelAutoRenewAutouploadModal';
